import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import {LoadingButton} from "@mui/lab"
import {Alert, Box, Button, CircularProgress, IconButton, Stack} from "@mui/material"
import Typography from "@mui/material/Typography"
import type {Editor} from "@tiptap/core"
import NewStatusPicker from "crqlar-react-common/dist/components/NewStatusPicker"
import UnsavedChangesDialog from "crqlar-react-common/dist/components/UnsavedChangesDialog"
import {useUnsavedChangesWarning} from "crqlar-react-common/dist/utils/useUnsavedChangesWarning"
import {Printout} from "crqlar-react-common/dist/models/printout/printout.model"
import {PrintoutService} from "crqlar-react-common/dist/services/printout/printout.service"
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util"
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly"
import {useLocalize} from "crqlar-react-common/dist/utils/text.util"
import {isEqual} from "lodash"
import {enqueueSnackbar} from "notistack"
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react"
import {useQuery} from "react-query"
import {useNavigate, useParams} from "react-router-dom"
import PrintoutDocumentRenderer from "../print/PrintoutDocumentRenderer"
import {usePrintoutPreviewContext} from "../print/usePrintoutPreviewContext"
import PrintoutBackupMenu from "./PrintoutBackupMenu"
import PrintoutEditorCanvas from "./PrintoutEditorCanvas"
import PrintoutInsertPanel from "./panels/PrintoutInsertPanel"
import PrintoutSettingsPanel from "./panels/PrintoutSettingsPanel"
import {
    DEFAULT_PRINTOUT_CONTENT,
    getPrintoutLanguages,
    parsePrintoutContent,
    resolvePrintoutDocument,
    setPrintoutDocumentContent,
} from "../core/printoutDocument"
import PrintoutLanguageMenu from "./PrintoutLanguageMenu"
import {loadPrintoutEditorDocument} from "./loadPrintoutEditorDocument"
import {useFullscreenSnackbarAnchor} from "../../layout/DashboardSnackbarProvider"

type EditorMode = "build" | "preview"

const INSERT_PANEL_WIDTH = 300
const SETTINGS_PANEL_WIDTH = 320
const MINIMUM_LOADING_DURATION_MS = 400

const PrintoutEditor = () => {
    useTrackScreenVisit("system printout editor")
    useFullscreenSnackbarAnchor()

    const {id: printoutId} = useParams()
    const navigate = useNavigate()
    const {t} = useLocalize()
    const readOnly = useReadOnly()

    const [draft, setDraft] = useState<Printout | undefined>(undefined)
    const [saved, setSaved] = useState<Printout | undefined>(undefined)
    const [mode, setMode] = useState<EditorMode>("build")
    const [editingLanguage, setEditingLanguage] = useState<string | undefined>()
    const [editor, setEditor] = useState<Editor | null>(null)
    const [editorActivated, setEditorActivated] = useState(false)
    const [editorReady, setEditorReady] = useState(false)
    const [minimumLoadingElapsed, setMinimumLoadingElapsed] = useState(false)
    const [busy, setBusy] = useState(false)
    const loadedPrintoutRef = useRef<Printout>()

    const printoutQuery = useQuery(
        ["printout", printoutId],
        () => PrintoutService.getPrintoutById(printoutId!),
        {enabled: !!printoutId, refetchOnWindowFocus: false},
    )

    useEffect(() => {
        const timeoutId = window.setTimeout(
            () => setMinimumLoadingElapsed(true),
            MINIMUM_LOADING_DURATION_MS,
        )

        return () => window.clearTimeout(timeoutId)
    }, [])

    useEffect(() => {
        // Fast Refresh can replay this effect without receiving a new server result.
        if (!printoutQuery.data || loadedPrintoutRef.current === printoutQuery.data) return
        loadedPrintoutRef.current = printoutQuery.data
        setDraft(printoutQuery.data)
        setSaved(printoutQuery.data)
        setEditingLanguage(undefined)
    }, [printoutQuery.data])

    const isDirty = !!draft && !!saved && !isEqual(draft, saved)
    const editingDisabled = readOnly || busy

    const notFound = !draft && (printoutQuery.isError || (printoutQuery.isSuccess && !printoutQuery.data))

    const languages = useMemo(() => getPrintoutLanguages(draft), [draft])
    const activeDocument = useMemo(
        () => resolvePrintoutDocument(draft, editingLanguage),
        [draft, editingLanguage],
    )
    const editorLoading = !minimumLoadingElapsed
        || printoutQuery.isLoading
        || !draft
        || (!!activeDocument && (!editor || !editorReady))
    const activeLanguage = activeDocument?.language
    const previewContext = usePrintoutPreviewContext(
        draft?.type,
        activeDocument?.language,
        draft?.scopedSectorIds,
    )

    const prepareEditorNavigation = useCallback(async () => {
        if (!editor || editor.isDestroyed) return

        loadPrintoutEditorDocument(editor, parsePrintoutContent(DEFAULT_PRINTOUT_CONTENT))

        // Let React remove complex node-view portals before EditorContent unmounts.
        await new Promise<void>(resolve => {
            window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve()))
        })
    }, [editor])

    const {showDialog, handleConfirmNavigation, handleCancelNavigation} =
        useUnsavedChangesWarning({
            hasUnsavedChanges: isDirty && !readOnly,
            isSaving: busy,
            prepareNavigation: prepareEditorNavigation,
        })

    const updateDocumentContent = useCallback((content: string) => {
        if (!activeLanguage) return

        setDraft(current => current
            ? setPrintoutDocumentContent(current, activeLanguage, content)
            : current)
    }, [activeLanguage])

    const notifyError = () =>
        enqueueSnackbar(t("Changes could not be saved. Check all fields!"), {
            variant: "error",
        })

    const notifySaved = () =>
        enqueueSnackbar(t("Changes saved!"), {
            variant: "success",
        })

    const saveDraft = async (): Promise<Printout | undefined> => {
        if (!draft) return undefined

        const result = await PrintoutService.updatePrintout(draft)
        setDraft(result)
        setSaved(result)
        return result
    }

    const handleSave = async () => {
        setBusy(true)
        try {
            await saveDraft()
            notifySaved()
        } catch (e) {
            console.log(e)
            notifyError()
        } finally {
            setBusy(false)
        }
    }

    const handleEditorInstanceChange = useCallback((instance: Editor | null) => {
        setEditor(instance)
        if (!instance) {
            setEditorActivated(false)
            setEditorReady(false)
        }
    }, [])

    const handleEditorReady = useCallback(() => {
        setEditorReady(true)
    }, [])

    const handleLanguageChange = useCallback((language: string) => {
        setMode("build")
        setEditingLanguage(language)
    }, [])

    const header = (
        <Stack
            direction={"row"}
            alignItems={"center"}
            gap={1}
            sx={{
                px: 2,
                minHeight: 56,
                py: {xs: 1, md: 0},
                flexShrink: 0,
                flexWrap: {xs: "wrap", md: "nowrap"},
                borderBottom: theme => `1px solid ${theme.palette.divider}`,
            }}
        >
            <Stack
                alignItems={"center"}
                direction={"row"}
                gap={1}
                sx={{flex: "1 1 240px", minWidth: 0}}
            >
                <IconButton
                    aria-label={t("Back")}
                    onClick={() => navigate(`/system/printouts/${printoutId}`)}
                >
                    <ArrowBackIcon/>
                </IconButton>

                <Typography variant={"h6"} noWrap>
                    {t("Printouts (new)")}
                </Typography>

                {!!draft?.name && (
                    <Typography
                        color={"text.secondary"}
                        sx={{minWidth: 0, overflow: "hidden", textOverflow: "ellipsis"}}
                        variant={"body1"}
                        noWrap
                    >
                        {draft.name}
                    </Typography>
                )}
            </Stack>

            <Stack
                alignItems={"center"}
                direction={"row"}
                gap={1}
                sx={{flexShrink: 0, ml: "auto"}}
            >
                {!!activeDocument && languages.length > 1 && (
                    <PrintoutLanguageMenu
                        disabled={editorLoading}
                        languages={languages}
                        onChange={handleLanguageChange}
                        value={activeDocument.language}
                    />
                )}

                <NewStatusPicker<EditorMode>
                    disabled={editorLoading || !activeDocument}
                    value={mode}
                    onChange={setMode}
                    ariaLabel={t("Editor mode")}
                    options={[
                        {value: "build", label: t("Build")},
                        {value: "preview", label: t("Preview")},
                    ]}
                />

                <LoadingButton
                    variant={"contained"}
                    loading={busy}
                    disabled={editorLoading || !isDirty || readOnly}
                    onClick={handleSave}
                >
                    {t("Save")}
                </LoadingButton>

                <PrintoutBackupMenu
                    editor={editorLoading ? null : editor}
                    language={activeDocument?.language}
                />
            </Stack>
        </Stack>
    )

    const sideColumnSx = {
        flexShrink: 0,
        minHeight: 0,
    } as const

    const buildMode = draft && activeDocument && (
        /*
            `minWidth: 0` is what keeps the three columns inside the window. Without it the row is a
            flex item at `min-width: auto`, so it sizes to the sum of its children's min-content
            widths — the two fixed side columns plus the canvas at its unscaled page width. On a
            1440px window that came to 1464 and pushed the settings panel off the right edge.
            Zero lets the row match the window; the canvas is the one column that may shrink, and it
            already scrolls its page.
        */
        <Stack direction={{xs: "column", lg: "row"}} sx={{flex: 1, minHeight: 0, minWidth: 0}}>
            <Box
                sx={theme => ({
                    ...sideColumnSx,
                    width: {xs: "100%", lg: INSERT_PANEL_WIDTH},
                    borderRight: {xs: "none", lg: `1px solid ${theme.palette.divider}`},
                    borderBottom: {xs: `1px solid ${theme.palette.divider}`, lg: "none"},
                })}
            >
                <PrintoutInsertPanel
                    editor={editor}
                    printoutType={draft.type}
                    disabled={editingDisabled || editorLoading}
                />
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: {xs: 420, lg: 0},
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <PrintoutEditorCanvas
                    content={activeDocument.content}
                    documentKey={activeDocument.language}
                    printout={draft}
                    onActivationChange={setEditorActivated}
                    onContentChange={updateDocumentContent}
                    onEditorChange={handleEditorInstanceChange}
                    onReady={handleEditorReady}
                />
            </Box>

            <Box
                sx={theme => ({
                    ...sideColumnSx,
                    width: {xs: "100%", lg: SETTINGS_PANEL_WIDTH},
                    borderLeft: {xs: "none", lg: `1px solid ${theme.palette.divider}`},
                    borderTop: {xs: `1px solid ${theme.palette.divider}`, lg: "none"},
                })}
            >
                <PrintoutSettingsPanel
                    editor={editorActivated ? editor : null}
                    disabled={editingDisabled || editorLoading}
                />
            </Box>
        </Stack>
    )

    const previewMode = draft && activeDocument && (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                bgcolor: "#eef2f7",
                p: {xs: 2, md: 4},
            }}
        >
            <PrintoutDocumentRenderer
                printout={draft}
                context={previewContext}
                language={activeDocument.language}
            />
        </Box>
    )

    const notFoundState = (
        <Stack alignItems={"center"} justifyContent={"center"} sx={{flex: 1, minHeight: 0, p: 3}}>
            <Alert
                action={
                    <Button color={"inherit"} onClick={() => navigate("/system/printouts")} size={"small"}>
                        {t("Back to printouts")}
                    </Button>
                }
                severity={"warning"}
            >
                {t("This printout does not exist or was deleted.")}
            </Alert>
        </Stack>
    )

    const noLanguagesState = (
        <Stack alignItems={"center"} justifyContent={"center"} sx={{flex: 1, minHeight: 0, p: 3}}>
            <Alert
                action={
                    <Button
                        color={"inherit"}
                        onClick={() => navigate(`/system/printouts/${printoutId}`)}
                        size={"small"}
                    >
                        {t("Open settings")}
                    </Button>
                }
                severity={"info"}
            >
                {t("Add a language in the printout settings before editing its layout.")}
            </Alert>
        </Stack>
    )

    const loadingState = (
        <Stack
            alignItems={"center"}
            aria-live={"polite"}
            justifyContent={"center"}
            role={"status"}
            spacing={2}
            sx={{flex: 1, minHeight: 0, bgcolor: "background.default"}}
        >
            <CircularProgress size={32} thickness={4} sx={{color: "text.secondary"}}/>
            <Typography color={"text.secondary"} variant={"body1"}>
                {t("Loading...")}
            </Typography>
        </Stack>
    )

    return (
        <Box
            sx={{
                height: "100dvh",
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.default",
            }}
        >
            {header}

            {notFound ? (
                notFoundState
            ) : !draft ? (
                loadingState
            ) : !activeDocument ? (
                minimumLoadingElapsed ? noLanguagesState : loadingState
            ) : (
                <Box
                    aria-busy={editorLoading}
                    sx={{position: "relative", flex: 1, minHeight: 0}}
                >
                    <Box
                        aria-hidden={editorLoading || mode !== "build"}
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            visibility: !editorLoading && mode === "build" ? "visible" : "hidden",
                            pointerEvents: !editorLoading && mode === "build" ? "auto" : "none",
                        }}
                    >
                        {buildMode}
                    </Box>

                    {mode === "preview" && (
                        <Box
                            aria-hidden={editorLoading}
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                visibility: editorLoading ? "hidden" : "visible",
                            }}
                        >
                            {previewMode}
                        </Box>
                    )}

                    {editorLoading && (
                        <Box sx={{position: "absolute", inset: 0, zIndex: 1, display: "flex"}}>
                            {loadingState}
                        </Box>
                    )}
                </Box>
            )}

            <UnsavedChangesDialog
                open={showDialog}
                onConfirm={handleConfirmNavigation}
                onCancel={handleCancelNavigation}
            />
        </Box>
    )
}

export default PrintoutEditor
